import React, { useCallback, useEffect, useMemo, useState } from 'react';
import BreadCrumb from 'Common/BreadCrumb';
import { Link } from 'react-router-dom';
import { Dropdown } from 'Common/Components/Dropdown';
import TableContainer from 'Common/TableContainer';
import moment from "moment";
import Select from 'react-select';

// Icons
import { Search, Trash2, Plus, MoreHorizontal, FileEdit, CheckCircle, Loader, X } from 'lucide-react';
import Modal from 'Common/Components/Modal';
import DeleteModal from 'Common/DeleteModal';

// Images

// react-redux
import { useDispatch, useSelector } from 'react-redux';
import { createSelector } from 'reselect';

// Formik
import * as Yup from "yup";
import { useFormik } from "formik";

import {
  onGetServiceList,
  onUpdateService,
  onAddService,
  onDeleteService,
} from 'slices/thunk';
import { ToastContainer } from 'react-toastify';
import filterDataBySearch from 'Common/filterDataBySearch';
import { currencyFormatter } from '../../helpers/formatter';
import CreatableSelect from 'react-select/creatable';
import { doGetAttendedBy, doGetServiceTypes } from '../../services/booking.service';
import { User } from '../../services/user.service';
import { doGetProjectSetting } from '../../services/project.service';
import ReactQuill from 'react-quill';
import { doFileUpload } from '../../services/file.service';
import DropZoneImage from '../../Common/Components/Dropzone';

const Services = () => {
  const dispatch = useDispatch<any>();

  const selectDataList = createSelector(
    (state: any) => state.Booking,
    (booking) => ({
      serviceList: booking.serviceList
    })
  );

  const { serviceList } = useSelector(selectDataList);
  const [service, setService] = useState<any>([]);
  const [eventData, setEventData] = useState<any>();

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [attendedByList, setAttendedByList] = useState<User[]>([])
  const [attendedByDroplist, setAttendedByDroplist] = useState<Array<{ value: string, label: string }>>([])

  const [show, setShow] = useState<boolean>(false);
  const [isEdit, setIsEdit] = useState<boolean>(false);

  const [serviceTypeDroplist, setServiceTypeDroplist] = useState<Array<{ value: string, label: string }>>([]);

  const [customerTypeList, setCustomerTypeList] = useState<Array<string>>([]);
  const [selectfiles, setSelectfiles] = useState<Array<File & { base64: string }>>([])

  // Get Data
  useEffect(() => {
    dispatch(onGetServiceList())
  }, [dispatch]);

  useEffect(() => {
    handleGetServiceTypes()
    handleListAttendedBy()
    setService(serviceList)
  }, [serviceList]);

  useEffect(() => {
    doGetProjectSetting().then((res: any) => {
      setCustomerTypeList(res.customerTypes || [])
    })
  }, [])

  const handleGetServiceTypes = () => {
    doGetServiceTypes().then((resp: any) => {
      const data = resp.map((item: any) => {
        return {
          value: item.serviceTypeId,
          label: item.type
        }
      });
      setServiceTypeDroplist(data);
    })
  }

  const handleListAttendedBy = () => {
    doGetAttendedBy().then((res) => {
      const users = res as unknown as User[]
      setAttendedByList(users)
      setAttendedByDroplist(users.map((user: User) => {
        return {
          value: user.userId,
          label: `${user.role} - ${user.name}`
        }
      }))
    })
  }

  // Delete Modal
  const [deleteModal, setDeleteModal] = useState<boolean>(false);
  const deleteToggle = () => setDeleteModal(!deleteModal);

  // Delete Data
  const onClickDelete = (cell: any) => {
    setDeleteModal(true);
    if (cell.serviceId) {
      setEventData(cell);
    }
  };

  const handleDelete = () => {
    if (eventData) {
      dispatch(onDeleteService(eventData.serviceId));
      setDeleteModal(false);
    }
  };

  // Update Data
  const handleUpdateDataClick = (ele: any) => {
    setEventData({ ...ele });
    setIsEdit(true);
    setShow(true);
  };

  // validation
  const validation: any = useFormik({
    // enableReinitialize : use this flag when initial values needs to be changed
    enableReinitialize: true,

    initialValues: {
      serviceId: (eventData && eventData.serviceId) || '',
      serviceType: (eventData && eventData.serviceType) || '',
      serviceTypeId: (eventData && eventData.serviceTypeId) || '',
      code: (eventData && eventData.code) || '',
      price: (eventData && eventData.price) || '',
      currency: (eventData && eventData.currency) || 'IDR',
      discount: (eventData && eventData.discount) || '',
      unitType: (eventData && eventData.unitType) || '',
      unitValue: (eventData && eventData.unitValue) || '',
      title: (eventData && eventData.title) || '',
      description: (eventData && eventData.description) || '',
      remark: (eventData && eventData.remark) || '',
      status: (eventData && eventData.status) || '',
      allowExternalBooking: (eventData && eventData.allowExternalBooking) || false,
      allowAttendedBy: (eventData && eventData.allowAttendedBy) || [],
      commissionPercentage: (eventData && eventData.commissionPercentage) || '',
      metadata: (eventData && eventData.metadata) || {},
      isSubscription: (eventData && eventData.isSubscription) || false,
      subscriptionLimit: (eventData && eventData.subscriptionLimit) || '',
      subscriptionDuration: (eventData && eventData.subscriptionDuration) || '',
      validFrom: (eventData && eventData.validFrom) || '',
      validTo: (eventData && eventData.validTo) || '',
      image: (eventData && eventData.image) || '',
      imageFile: (eventData && eventData.imageFile) || {},
    },
    validationSchema: Yup.object({
      serviceType: Yup.string().required("Please Enter Service Type"),
      code: Yup.string().required("Please Enter Code"),
      currency: Yup.string().required("Please Enter Currency"),
      price: Yup.number().required("Please Enter Price"),
      discount: Yup.number().optional(),
      unitType: Yup.string().optional(),
      unitValue: Yup.string().required("Please Enter Unit Value"),
      title: Yup.string().required("Please Enter Title"),
      description: Yup.string().required("Please Enter Description"),
      remark: Yup.string().optional(),
      status: Yup.string().optional(),
      allowExternalBooking: Yup.boolean().default(false),
      allowAttendedBy: Yup.array().of(Yup.string()).optional(),
      commissionPercentage: Yup.number().optional(),
      isSubscription: Yup.boolean().default(false),
      subscriptionLimit: Yup.number().when('isSubscription', {
        is: true,
        then: (schema) => schema.required("Subscription Limit is required").min(1, "Subscription Limit must be greater than 0"),
        otherwise: (schema) => schema.optional()
      }),
      subscriptionDuration: Yup.number().when('isSubscription', {
        is: true,
        then: (schema) => schema.required("Subscription Duration is required").min(1, "Subscription Duration must be greater than 0"),
        otherwise: (schema) => schema.optional()
      }),
      validFrom: Yup.date().optional(),
      validTo: Yup.date().optional(),
      image: Yup.string().optional(),
    }),

    onSubmit: async (values) => {
      const image = []
      if (selectfiles.length > 0) {
        for (const file of selectfiles) {
          const fileObject = await doFileUpload(file.name, file.base64)
          image.push(fileObject.fileId)
        }
        values.image = image[0]
      }
      const data = {
        ...values,
      };
      if (isEdit) {
        dispatch(onUpdateService(data));
      } else {
        delete data.serviceId;
        dispatch(onAddService(data));
      }
      toggle();
    },
  });

  const currencySelect = [
    { value: 'IDR', label: 'Rupiah' },
  ];

  //
  const toggle = useCallback(() => {
    if (show) {
      setShow(false);
      setEventData("");
      setIsEdit(false);
      // setSelectedImage('');
    } else {
      setShow(true);
      setEventData("");
      // setSelectedImage('');
      validation.resetForm();
    }
  }, [show, validation]);

  // Search Data
  const filterSearchData = (e: any) => {
    const search = e.target.value;
    const keysToSearch = ['code', 'title', 'serviceType'];
    filterDataBySearch(serviceList, search, keysToSearch, setService);
  };

  // columns
  const Status = ({ item }: any) => {
    switch (item) {
      case "active":
        return (<span className="px-2.5 py-0.5 text-xs font-medium rounded border bg-green-100 border-transparent text-green-500 dark:bg-green-500/20 dark:border-transparent inline-flex items-center status"><CheckCircle className="w-3 h-3 mr-1.5" />{item}</span>);
      case "waiting":
        return (<span className="px-2.5 py-0.5 inline-flex items-center text-xs font-medium rounded border bg-slate-100 border-transparent text-slate-500 dark:bg-slate-500/20 dark:text-zink-200 dark:border-transparent status"><Loader className="w-3 h-3 mr-1.5" />{item}</span>);
      case "inactive":
        return (<span className="px-2.5 py-0.5 inline-flex items-center text-xs font-medium rounded border bg-red-100 border-transparent text-red-500 dark:bg-red-500/20 dark:border-transparent status"><X className="w-3 h-3 mr-1.5" />{item}</span>);
      default:
        return (<span className="px-2.5 py-0.5 text-xs font-medium rounded border bg-green-100 border-transparent text-green-500 dark:bg-green-500/20 dark:border-transparent inline-flex items-center status"><CheckCircle className="w-3 h-3 mr-1.5" />{item}</span>);
    }
  };

  const columns = useMemo(() => [
    {
      header: (
        <div className="flex items-center h-full">
          <input id="CheckboxAll" className="w-4 h-4 bg-white border border-slate-200 checked:bg-none dark:bg-zink-700 dark:border-zink-500 rounded-sm appearance-none arrow-none relative after:absolute after:content-['\eb7b'] after:top-0 after:left-0 after:font-remix after:leading-none after:opacity-0 checked:after:opacity-100 after:text-custom-500 checked:border-custom-500 dark:after:text-custom-500 dark:checked:border-custom-800 cursor-pointer" type="checkbox" />
        </div>
      ),
      enableSorting: false,
      id: "checkAll",
      cell: (cell: any) => {
        return (
          <div className="flex items-center h-full">
            <input id="Checkbox1" className="w-4 h-4 bg-white border border-slate-200 checked:bg-none dark:bg-zink-700 dark:border-zink-500 rounded-sm appearance-none arrow-none relative after:absolute after:content-['\eb7b'] after:top-0 after:left-0 after:font-remix after:leading-none after:opacity-0 checked:after:opacity-100 after:text-custom-500 checked:border-custom-500 dark:after:text-custom-500 dark:checked:border-custom-800 cursor-pointer" type="checkbox" />
          </div>
        );
      }
    },
    {
      header: "Title",
      accessorKey: "title",
      enableColumnFilter: false,
      cell: (cell: any) => (
        <div className="flex items-center gap-2">
          <div className="grow">
            <h6 className="mb-1">{cell.getValue()}</h6>
          </div>
        </div>
      ),
    },
    {
      header: "Code",
      accessorKey: "code",
      enableColumnFilter: false
    },
    {
      header: "Type",
      accessorKey: "serviceType",
      enableColumnFilter: false
    },
    {
      header: "Price",
      accessorKey: "price",
      enableColumnFilter: false,
      cell: (cell: any) => (
        <span>{currencyFormatter(cell.getValue())}</span>
      )
    },
    {
      header: "Created At",
      accessorKey: "createdAt",
      enableColumnFilter: false,
      cell: (cell: any) => (
        <span>{moment(cell.getValue()).format("DD MMM YYYY")}</span>
      ),
    },
    {
      header: "Status",
      accessorKey: "status",
      enableColumnFilter: false,
      enableSorting: true,
      cell: (cell: any) => (
        <Status item={cell.getValue()} />
      ),
    },
    {
      header: "Action",
      enableColumnFilter: false,
      enableSorting: true,
      cell: (cell: any) => (
        <Dropdown className="relative">
          <Dropdown.Trigger className="flex items-center justify-center w-[30px] h-[30px] p-0 text-slate-500 btn bg-slate-100 hover:text-white hover:bg-slate-600 focus:text-white focus:bg-slate-600 focus:ring focus:ring-slate-100 active:text-white active:bg-slate-600 active:ring active:ring-slate-100 dark:bg-slate-500/20 dark:text-slate-400 dark:hover:bg-slate-500 dark:hover:text-white dark:focus:bg-slate-500 dark:focus:text-white dark:active:bg-slate-500 dark:active:text-white dark:ring-slate-400/20" id={cell.row.original.serviceId}>
            <MoreHorizontal className="w-3 h-3" />
          </Dropdown.Trigger>
          <Dropdown.Content placement="right-end" className="absolute z-50 py-2 mt-1 ltr:text-left rtl:text-right list-none bg-white rounded-md shadow-md min-w-[10rem] dark:bg-zink-600" aria-labelledby={cell.row.original.serviceId}>
            <li>
              <Link data-modal-target="addUserModal" className="block px-4 py-1.5 text-base transition-all duration-200 ease-linear text-slate-600 hover:bg-slate-100 hover:text-slate-500 focus:bg-slate-100 focus:text-slate-500 dark:text-zink-100 dark:hover:bg-zink-500 dark:hover:text-zink-200 dark:focus:bg-zink-500 dark:focus:text-zink-200" to="#!"
                onClick={() => {
                  const data = cell.row.original;
                  handleUpdateDataClick(data);
                }}>
                <FileEdit className="inline-block w-3 h-3 ltr:mr-1 rtl:ml-1" /> <span className="align-middle">Edit</span></Link>
            </li>
            {
              cell.row.original.status === 'active' &&
              <li>
                <Link className="block px-4 py-1.5 text-base transition-all duration-200 ease-linear text-slate-600 hover:bg-slate-100 hover:text-slate-500 focus:bg-slate-100 focus:text-slate-500 dark:text-zink-100 dark:hover:bg-zink-500 dark:hover:text-zink-200 dark:focus:bg-zink-500 dark:focus:text-zink-200" to="#!" onClick={() => {
                  const orderData = cell.row.original;
                  onClickDelete(orderData);
                }}><Trash2 className="inline-block w-3 h-3 ltr:mr-1 rtl:ml-1" /> <span className="align-middle">Delete</span></Link>
              </li>
            }
          </Dropdown.Content>
        </Dropdown>
      ),
    }
  ], []
  );

  const options = [
    { value: 'Select Status', label: 'Select Status' },
    { value: 'active', label: 'Active' },
    { value: 'inactive', label: 'Inactive' },
    { value: 'rejected', label: 'Rejected' },
    { value: 'hidden', label: 'Hidden' },
  ];

  const handleChange = (selectedOption: any) => {
    if (selectedOption.value === 'Select Status' || selectedOption.value === 'Hidden') {
      setService(serviceList);
    } else {
      const filteredServices = serviceList.filter((data: any) => data.status === selectedOption.value);
      setService(filteredServices);
    }
  };

  return (
    <React.Fragment>
      <BreadCrumb title='Services' pageTitle='Services' />
      <DeleteModal show={deleteModal} onHide={deleteToggle} onDelete={handleDelete} />
      <ToastContainer closeButton={false} limit={1} />
      <div className="grid grid-cols-1 gap-x-5 xl:grid-cols-12">
        <div className="xl:col-span-12">
          <div className="card" id="usersTable">
            <div className="card-body">
              <div className="flex items-center">
                <h6 className="text-15 grow">Service List</h6>
                <div className="shrink-0">
                  <button type="button" className="text-white btn bg-custom-500 border-custom-500 hover:text-white hover:bg-custom-600 hover:border-custom-600 focus:text-white focus:bg-custom-600 focus:border-custom-600 focus:ring focus:ring-custom-100 active:text-white active:bg-custom-600 active:border-custom-600 active:ring active:ring-custom-100 dark:ring-custom-400/20" onClick={toggle}><Plus className="inline-block w-4 h-4" />
                    <span className="align-middle">Add Service</span>
                  </button>
                </div>
              </div>
            </div>
            <div className="!py-3.5 card-body border-y border-dashed border-slate-200 dark:border-zink-500">
              <form action="#!">
                <div className="grid grid-cols-1 gap-5 xl:grid-cols-12">
                  <div className="relative xl:col-span-2">
                    <input type="text" className="ltr:pl-8 rtl:pr-8 search form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200" placeholder="Search for name, email, phone number etc..." autoComplete="off" onChange={(e) => filterSearchData(e)} />
                    <Search className="inline-block w-4 h-4 absolute ltr:left-2.5 rtl:right-2.5 top-2.5 text-slate-500 dark:text-zink-200 fill-slate-100 dark:fill-zink-600" />
                  </div>
                  <div className="xl:col-span-2">
                    <Select
                      className="border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
                      options={options}
                      isSearchable={false}
                      defaultValue={options[0]}
                      onChange={(event: any) => handleChange(event)}
                      id="choices-single-default"
                    />
                  </div>
                </div>
              </form>
            </div>
            <div className="card-body">
              {service && service.length > 0 ?
                <TableContainer
                  isPagination={true}
                  columns={(columns || [])}
                  data={(service || [])}
                  customPageSize={10}
                  divclassName="-mx-5 -mb-5 overflow-x-auto"
                  tableclassName="w-full border-separate table-custom border-spacing-y-1 whitespace-nowrap"
                  theadclassName="text-left relative rounded-md bg-slate-100 dark:bg-zink-600 after:absolute ltr:after:border-l-2 rtl:after:border-r-2 ltr:after:left-0 rtl:after:right-0 after:top-0 after:bottom-0 after:border-transparent [&.active]:after:border-custom-500 [&.active]:bg-slate-100 dark:[&.active]:bg-zink-600"
                  thclassName="px-3.5 py-2.5 first:pl-5 last:pr-5 font-semibold"
                  tdclassName="px-3.5 py-2.5 first:pl-5 last:pr-5"
                  PaginationClassName="flex flex-col items-center mt-8 md:flex-row"
                />
                :
                (<div className="noresult">
                  <div className="py-6 text-center">
                    <Search className="w-6 h-6 mx-auto text-sky-500 fill-sky-100 dark:sky-500/20" />
                    <h5 className="mt-2 mb-1">Sorry! No Result Found</h5>
                    <p className="mb-0 text-slate-500 dark:text-zink-200">We've searched more than 199+ users We did not find any users for you search.</p>
                  </div>
                </div>)}
            </div>
          </div>
        </div>
      </div>

      <Modal show={show} onHide={toggle} id="defaultModal" modal-center="true"
        className="fixed flex flex-col transition-all duration-300 ease-in-out left-2/4 z-drawer -translate-x-2/4 -translate-y-2/4"
        dialogClassName="w-screen md:w-[30rem] bg-white shadow rounded-md dark:bg-zink-600">
        <Modal.Header className="flex items-center justify-between p-4 border-b dark:border-zink-300/20"
          closeButtonClass="transition-all duration-200 ease-linear text-slate-400 hover:text-red-500">
          <Modal.Title className="text-16">{!!isEdit ? "Edit Service" : "Add Service"}</Modal.Title>
        </Modal.Header>
        <Modal.Body className="max-h-[calc(theme('height.screen')_-_180px)] p-4 overflow-y-auto">
          <form action="#!" onSubmit={(e) => {
            e.preventDefault();
            validation.handleSubmit();
            return false;
          }}>
            <div className="mb-3">
              <label htmlFor="image" className="inline-block mb-2 text-base font-medium">Image</label>
              <DropZoneImage
                selectfiles={selectfiles}
                setSelectfiles={setSelectfiles}
                formikData={validation}
                fieldName='image'
                files={validation.values.imageFile}
                maxFiles={1}
              />
            </div>
            <div className="mb-3">
              <label htmlFor="typeInput" className="inline-block mb-2 text-base font-medium">Service Type</label>
              <CreatableSelect isClearable
                name="serviceType"
                options={serviceTypeDroplist}
                onChange={(selectedOption) => validation.setFieldValue(`serviceType`, selectedOption?.value)}
                value={serviceTypeDroplist.find(option => option.value === validation.values.serviceTypeId)}
              />
              {validation.touched.serviceType && validation.errors.serviceType ? (
                <p className="text-red-400">{validation.errors.serviceType}</p>
              ) : null}
            </div>
            <div className="mb-3">
              <label htmlFor="codeInput" className="inline-block mb-2 text-base font-medium">Code</label>
              <input type="text" id="codeInput" className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
                placeholder="Enter Code"
                name="code"
                onChange={validation.handleChange}
                value={validation.values.code || ""}
              />
              {validation.touched.code && validation.errors.code ? (
                <p className="text-red-400">{validation.errors.code}</p>
              ) : null}
            </div>
            <div className="mb-3">
              <label htmlFor="titleInput" className="inline-block mb-2 text-base font-medium">Title</label>
              <input type="text" id="titleInput" className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
                placeholder="Enter Title"
                name="title"
                onChange={validation.handleChange}
                value={validation.values.title || ""}
              />
              {validation.touched.title && validation.errors.title ? (
                <p className="text-red-400">{validation.errors.title}</p>
              ) : null}
            </div>
            <div className="mb-3">
              <label htmlFor="currencyInput" className="inline-block mb-2 text-base font-medium">Currency</label>
              <Select
                className="border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
                options={currencySelect}
                isSearchable={false}
                name="currency"
                id="currencyInput"
                onChange={selectedOption => {
                  validation.setFieldValue(`currency`, selectedOption?.value)
                }}
                value={currencySelect.find(currency => { return currency.value === validation.values.currency })}
              />
              {validation.touched.currency && validation.errors.currency ? (
                <p className="text-red-400">{validation.errors.currency}</p>
              ) : null}
            </div>
            <div className="mb-3">
              <label htmlFor="priceInput" className="inline-block mb-2 text-base font-medium">Price</label>
              <input type="text" id="priceInput" className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
                placeholder="Enter Price"
                name="price"
                onChange={validation.handleChange}
                value={validation.values.price || ""}
              />
              {validation.touched.price && validation.errors.price ? (
                <p className="text-red-400">{validation.errors.price}</p>
              ) : null}
            </div>
            {
              customerTypeList.length > 0 && customerTypeList.map((type: string, index: number) => (
                <div className="mb-3" key={index}>
                  <label htmlFor="priceInput" className="inline-block mb-2 text-base font-medium">Price ({type})</label>
                  <input
                    type="text"
                    id={`priceInput.${type}`}
                    className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
                    placeholder="Enter Price"
                    name={`metadata.price.${type}`}
                    onChange={validation.handleChange}
                    value={validation.values.metadata?.price?.[type] || ""}
                  />
                </div>
              ))
            }
            <div className="mb-3">
              <label htmlFor="descriptionInput" className="inline-block mb-2 text-base font-medium">Description</label>
              <ReactQuill
                id={`descriptionInput`}
                placeholder="Enter Description"
                className="w-full border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
                theme="snow"
                value={validation.values.description || ""}
                onChange={(value) => {
                  validation.setFieldValue(`description`, value)
                }}
              />
              {validation.touched.description && validation.errors.description ? (
                <p className="text-red-400">{validation.errors.description}</p>
              ) : null}
            </div>
            <div className="mb-3">
              <label htmlFor="unitValueInput" className="inline-block mb-2 text-base font-medium">Duration</label>
              <input type="text" id="unitValueInput" className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
                placeholder="Enter Duration"
                name="unitValue"
                onChange={validation.handleChange}
                value={validation.values.unitValue || ""}
              />
              {validation.touched.unitValue && validation.errors.unitValue ? (
                <p className="text-red-400">{validation.errors.unitValue}</p>
              ) : null}
            </div>
            <div className="mb-3">
              <label htmlFor="remarkInput" className="inline-block mb-2 text-base font-medium">Remark</label>
              <input type="text" id="remarkInput" className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
                placeholder="Enter Remark"
                name="remark"
                onChange={validation.handleChange}
                value={validation.values.remark || ""}
              />
              {validation.touched.remark && validation.errors.remark ? (
                <p className="text-red-400">{validation.errors.remark}</p>
              ) : null}
            </div>
            <div className="mb-3">
              <label htmlFor="commissionPercentageInput" className="inline-block mb-2 text-base font-medium">Commission Percentage</label>
              <input type="text" id="commissionPercentageInput" className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
                placeholder="Enter Commission Percentage"
                name="commissionPercentage"
                onChange={validation.handleChange}
                value={validation.values.commissionPercentage || ""}
              />
              {validation.touched.commissionPercentage && validation.errors.commissionPercentage ? (
                <p className="text-red-400">{validation.errors.commissionPercentage}</p>
              ) : null}
            </div>
            <div className="mb-3">
              <label htmlFor="allowAttendedByInput" className="inline-block mb-2 text-base font-medium">Allowed to be attended by</label>
              <Select
                isMulti={true}
                className="border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
                options={attendedByDroplist}
                isSearchable={false}
                name="allowAttendedBy"
                id="allowAttendedByInput"
                onChange={selectedOption => {
                  validation.setFieldValue(`allowAttendedBy`, selectedOption?.map((item: any) => item.value))
                }}
                value={attendedByDroplist.filter(allowAttendedBy => { return validation.values.allowAttendedBy.includes(allowAttendedBy.value) })}
              />
              {validation.touched.commissionPercentage && validation.errors.allowAttendedBy ? (
                <p className="text-red-400">{validation.errors.allowAttendedBy}</p>
              ) : null}
            </div>
            <div className="mb-3 flex items-center gap-3">
              <label
                htmlFor={`allowExternalBookingOption`}
                className="flex flex-col gap-4 p-5 border rounded-md cursor-pointer md:flex-row border-slate-200 dark:border-zink-500 peer-checked:border-purple-500 dark:peer-checked:border-purple-700">
                <span className="grow flex items-center gap-3">
                  <input id={`allowExternalBookingOption`}
                    className="w-4 h-4 border appearance-none cursor-pointer bg-slate-100 border-slate-200 dark:bg-zink-600 dark:border-zink-500 checked:bg-purple-500 checked:border-purple-500 dark:checked:bg-purple-500 dark:checked:border-purple-500 checked:disabled:bg-purple-400 checked:disabled:border-purple-400 peer"
                    type="checkbox"
                    name="allowExternalBooking"
                    checked={validation.values.allowExternalBooking}
                    onChange={validation.handleChange} />
                  <span className="block font-semibold text-15">Allow External Booking</span>
                </span>
              </label>
            </div>
            <div className="mb-3">
              <label htmlFor="validFromInput" className="inline-block mb-2 text-base font-medium">Valid From</label>
              <input type="date" id="validFromInput" className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
                placeholder="Enter Valid From"
                name="validFrom"
                onChange={validation.handleChange}
                value={moment(validation.values.validFrom).format("YYYY-MM-DD") || ""}
              />
            </div>
            <div className="mb-3">
              <label htmlFor="validToInput" className="inline-block mb-2 text-base font-medium">Valid To</label>
              <input type="date" id="validToInput" className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
                placeholder="Enter Valid To"
                name="validTo"
                onChange={validation.handleChange}
                value={moment(validation.values.validTo).format("YYYY-MM-DD") || ""}
              />
            </div>
            <div className="mb-3 flex items-center gap-3">
              <label
                htmlFor={`isSubscriptionOption`}
                className="flex flex-col gap-4 p-5 border rounded-md cursor-pointer md:flex-row border-slate-200 dark:border-zink-500 peer-checked:border-purple-500 dark:peer-checked:border-purple-700">
                <span className="grow flex items-center gap-3">
                  <input id={`isSubscriptionOption`}
                    className="w-4 h-4 border appearance-none cursor-pointer bg-slate-100 border-slate-200 dark:bg-zink-600 dark:border-zink-500 checked:bg-purple-500 checked:border-purple-500 dark:checked:bg-purple-500 dark:checked:border-purple-500 checked:disabled:bg-purple-400 checked:disabled:border-purple-400 peer"
                    type="checkbox"
                    name="isSubscription"
                    checked={validation.values.isSubscription}
                    onChange={validation.handleChange} />
                  <span className="block font-semibold text-15">Subscription</span>
                </span>
              </label>
            </div>
            {
              validation.values.isSubscription && (
                <>
                  <div className="mb-3">
                    <label htmlFor="subscriptionLimitInput" className="inline-block mb-2 text-base font-medium">Subscription Limit</label>
                    <input
                      type="number"
                      id="subscriptionLimitInput"
                      className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
                      placeholder="Enter Subscription Limit"
                      name="subscriptionLimit"
                      onChange={validation.handleChange}
                      value={validation.values.subscriptionLimit || ""}
                    />
                    {validation.touched.subscriptionLimit && validation.errors.subscriptionLimit ? (
                      <p className="text-red-400">{validation.errors.subscriptionLimit}</p>
                    ) : null}
                  </div>
                  <div className="mb-3">
                    <label htmlFor="subscriptionDurationInput" className="inline-block mb-2 text-base font-medium">Subscription Expiry (days)</label>
                    <input
                      type="number"
                      id="subscriptionDurationInput"
                      className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
                      placeholder="Enter Subscription Duration"
                      name="subscriptionDuration"
                      onChange={validation.handleChange}
                      value={validation.values.subscriptionDuration || ""}
                    />
                    {validation.touched.subscriptionDuration && validation.errors.subscriptionDuration ? (
                      <p className="text-red-400">{validation.errors.subscriptionDuration}</p>
                    ) : null}
                  </div>
                </>
              )
            }
            <div className="flex justify-end gap-2 mt-4">
              <button type="reset" data-modal-close="addDocuments" className="text-red-500 transition-all duration-200 ease-linear bg-white border-white btn hover:text-red-600 focus:text-red-600 active:text-red-600 dark:bg-zink-500 dark:border-zink-500" onClick={toggle}>Cancel</button>
              <button type="submit" className="text-white transition-all duration-200 ease-linear btn bg-custom-500 border-custom-500 hover:text-white hover:bg-custom-600 hover:border-custom-600 focus:text-white focus:bg-custom-600 focus:border-custom-600 focus:ring focus:ring-custom-100 active:text-white active:bg-custom-600 active:border-custom-600 active:ring active:ring-custom-100 dark:ring-custom-400/20">
                {!!isEdit ? "Update Service" : "Add Service"}
              </button>
            </div>
          </form>
        </Modal.Body>
      </Modal>
    </React.Fragment>
  );
};

export default Services;
